import { queries } from "@/graphql";
import { useQuery } from "@apollo/client/react";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo } from "react";
import type {
  QueryGetSubscriptionPlansArgs,
  SubscriptionPlanType,
} from "@/types/graphql";

/**
 * Custom hook to fetch the list of subscriptions.
 * @param {object} props - The properties to be passed to the query.
 * @returns {{subscriptionsList: Array<{id: string, name: string}>, loading: boolean}} - The list of subscriptions and the loading state.
 */
export function useSubscriptionsList(
  props: QueryGetSubscriptionPlansArgs = {},
) {
  const propsKey = JSON.stringify(props);
  const variables: QueryGetSubscriptionPlansArgs = useMemo(
    () => ({
      ...props,
    }),
    [propsKey],
  );

  const { data, loading, error } = useQuery<
    {
      getSubscriptionPlans: Array<SubscriptionPlanType | null> | null;
    },
    QueryGetSubscriptionPlansArgs
  >(queries.getSubscriptionPlans, {
    variables,
    errorPolicy: "all",
  });

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    }
  }, [error]);

  const subscriptionsList = useMemo(
    () =>
      (data?.getSubscriptionPlans || [])
        .filter((subscription): subscription is SubscriptionPlanType =>
          Boolean(subscription),
        )
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0)),
    [data?.getSubscriptionPlans],
  );

  return {
    subscriptionsList,
    loading,
  };
}
